// Kiosk Ayarları penceresi (10 sekme) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'sales.kioskSettings';

export const kioskSettingsGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'Kiosk ayarları nasıl yapılır?'),
  intro: t(`${G}.intro`, 'Bu pencerede bir kioskun müşteriye gösterdiği her şeyi (reklam, kategori, ürün) ve arka plan ayarlarını (fiş, yazarkasa, cihaz, ödeme, mutfak) yönetirsiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Kiosk Ayarları'),
    tabs: t(`${G}.regions.tabs`, 'Sekmeler'),
    body: t(`${G}.regions.body`, 'Sekme içeriği'),
    footer: t(`${G}.regions.footer`, 'İptal / Değişiklikleri Kaydet'),
  },
  navItems: [
    t(`${G}.nav.ads`, 'Reklam'),
    t(`${G}.nav.categories`, 'Kategoriler'),
    t(`${G}.nav.products`, 'Ürünler'),
    t(`${G}.nav.additional`, 'Ek Ayarlar'),
    t(`${G}.nav.personnel`, 'Personel'),
    t(`${G}.nav.receipt`, 'Adisyon Ayarları'),
    t(`${G}.nav.fiscal`, 'Yazarkasa'),
    t(`${G}.nav.devices`, 'Cihaz Yönetimi'),
    t(`${G}.nav.payment`, 'Ödeme Ayarları'),
    t(`${G}.nav.kitchen`, 'Mutfak Ekranı'),
  ],
  steps: [
    {
      region: 'tabs',
      title: t(`${G}.s1.title`, '1. Sekmeler'),
      text: t(`${G}.s1.text`, 'Ayarlar 10 sekmeye ayrılmıştır: Reklam, Kategoriler, Ürünler, Ek Ayarlar, Personel, Adisyon Ayarları, Yazarkasa, Cihaz Yönetimi, Ödeme Ayarları ve Mutfak Ekranı. Mobilde sekmeyi üstteki "Değiştir" düğmesiyle seçersiniz.'),
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Reklam'),
      text: t(`${G}.s2.text`, 'Ana Ekran\'da tam ekran reklamlar döner; "Sipariş Ver" düğmesi reklamın üzerinde görünür ve metni, rengi, boyutu ayarlanabilir. Sipariş Ekranı\'nın üstünde ise ekranın %12\'sini kaplayan yatay banner reklamlar gösterilir.'),
      tips: [
        t(`${G}.s2.tip1`, 'Önerilen tam ekran görsel: 21" 1080x1920, 24" 1200x1920, 27" 1440x2560 px.'),
        t(`${G}.s2.tip2`, 'Video seçerseniz süre bilgi amaçlıdır; video kendi süresi kadar oynatılır.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Kategoriler ve Ürünler'),
      text: t(`${G}.s3.text`, 'Kategoriler sekmesinde sipariş ekranının sol tarafında dikey olarak gösterilecek ürün kategorilerini yönetirsiniz; her kategoriye bir isim ve resim eklenir, ikisi birlikte gösterilir. Ürünler sekmesinde her kategoriye "Ürün Ekle" ile ürün eklenir; önce en az bir kategori olmalıdır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Kategori resmi resim kütüphanesinden seçilir; kare (1:1) format önerilir.'),
        t(`${G}.s3.tip2`, 'Ürüne "Ek Ürünler" (yanında gidebilecek ürünler) ve özelleştirme seçenekleri tanımlayabilirsiniz.'),
        t(`${G}.s3.tip3`, '"Kategori ve Ürün Taslakları" bu tarayıcıya kaydedilir; başka bilgisayarda görünmez.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Ek Ayarlar ve Personel'),
      text: t(`${G}.s4.text`, 'Ek Ayarlar\'da Paket Sipariş / Burada Yiyeceğim seçeneklerini açıp kapatırsınız (en az biri açık kalmalı; yalnız biri açıksa seçim ekranı atlanır), ürün sipariş modunu (Hızlı Sepet Ekleme veya Detaylı Ürün Seçimi) ve Sepete Ekle düğmesinin görünümünü seçersiniz. Personel\'de bu kioskun siparişlerini yönetecek personeli seçip yetkilerini verirsiniz.'),
      tips: [
        t(`${G}.s4.tip1`, 'Personel yetkileri: Kiosk Yönetim Erişimi, Tüm Siparişleri Görme, Sipariş Durumu Güncelleme, Sipariş İptal Etme.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Adisyon Ayarları ve Yazarkasa'),
      text: t(`${G}.s5.text`, 'Adisyon Ayarları sekmesi, kiosktan verilen siparişlerin fiş/adisyon çıktısının mevcut ayarlarını özetler; düzenlemek için "Adisyon Ayarlarını Düzenle" düğmesine basın. Yazarkasa sekmesinde kullanılacak modeli seçin: PavoPay, Hugin T300 veya Inpos M530 (yazarkasa kullanımı isteğe bağlıdır).'),
      tips: [
        t(`${G}.s5.tip1`, 'PavoPay: POS bilgilerini girip kaydedin; eşleştirme Kiosk uygulaması açılırken otomatik yapılır. "Direkt POS (REST)" tipinde POS cihazı ile Kiosk aynı yerel ağda olmalıdır ("Pavo Cihaz Üzerinden (REST-P2P)" tipinde gerekmez). Local Service gerekmez; kredi kartı ve nakit desteklenir, e-fiş otomatik kesilir.'),
        t(`${G}.s5.tip2`, 'Hugin T300 / Inpos M530: yazarkasa ile HTTP API üzerinden konuşulur; yazarkasa ile aynı ağdaki bir bilgisayarda GTS Local Service çalışıyor olmalıdır. Adrese yalnız IP ve port yazın, sonra "Bağlantıyı Test Et" ile deneyin.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s6.title`, '6. Cihaz Yönetimi, Ödeme ve Mutfak Ekranı'),
      text: t(`${G}.s6.text`, 'Cihaz Yönetimi\'nde "Cihaz Ekle" her cihaz için 11 haneli bir giriş kodu üretir; kiosk uygulamasına bu kodla girilir. Ödeme Ayarları\'nda kiosk satışlarının muhasebe kaydı için varsayılan cari, POS banka hesabı ve stoğun düşeceği depo seçilir. Mutfak Ekranı\'nda siparişlerin düşeceği QR menüyü seçersiniz; boş bırakılırsa şubedeki ilk QR menü kullanılır.'),
      tips: [
        t(`${G}.s6.tip1`, 'Seçtiğiniz varsayılan depo başka bir şubeye bağlıysa, kayıtta bu depo kioskun şubesine bağlanır.'),
        t(`${G}.s6.tip2`, 'Cihaz Yönetimi ve Mutfak Ekranı değişiklikleri kendi düğmeleriyle hemen kaydedilir; bu sekmelerde altta yalnız "Kapat" görünür.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s7.title`, '7. Değişiklikleri kaydedin'),
      text: t(`${G}.s7.text`, 'Diğer tüm sekmelerdeki değişiklikler, alttaki "Değişiklikleri Kaydet" düğmesine basınca birlikte kaydedilir ve pencere kapanır. Düğme yalnız bir değişiklik yaptığınızda açılır. "İptal" kaydetmeden kapatır.'),
    },
  ],
});
